import React, { useState } from 'react';
import { relativeDayLabel } from '../utils/timezone';

// Shared per-entry completion history list (see docs/ux/completion-undo.md).
// Single source of truth for both the On Demand video card and the player modal
// so the two surfaces never diverge. Renders the most recent sessions with a
// confirm-gated per-row Remove. Owns its own confirm/removing state; the OWNER
// supplies the list (`sessions`), a `loading` flag, and an `onRemove(id)` that
// performs the delete + decrement and returns a boolean success (the owner also
// splices its own list on success). All clicks stopPropagation so the list can
// live inside a clickable card without triggering the card's onClick.
//
// Top-level component (NOT defined inside another component) — no remount/focus
// pitfall (see feedback_no_inner_components).
const SOURCE_LABEL = { live: 'Live', ondemand: 'On Demand', program: 'Program', manual: 'Logged' };

function CompletionHistoryList({ sessions, loading = false, onRemove, copy = {} }) {
    const [confirmRemoveId, setConfirmRemoveId] = useState(null);
    const [removingId, setRemovingId] = useState(null);

    const stop = (e) => { if (e && e.stopPropagation) e.stopPropagation(); };

    const handleConfirm = async (e, id) => {
        stop(e);
        if (typeof onRemove !== 'function') return;
        setRemovingId(id);
        let ok = false;
        try { ok = await onRemove(id); } catch (_) { ok = false; }
        if (ok) setConfirmRemoveId(null); // failure keeps the confirm row so the member can retry
        setRemovingId(null);
    };

    if (loading) {
        return (
            <div style={{ marginTop: 8 }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite' }} />
            </div>
        );
    }
    if (!sessions || sessions.length === 0) {
        return <div style={{ marginTop: 8 }}><span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 12, color: 'var(--mom-warm-gray)' }}>—</span></div>;
    }

    return (
        <div style={{ marginTop: 8 }}>
            {sessions.slice(0, 8).map((entry) => (
                <div key={entry.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--mom-cream-dark)' }}>
                    <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)' }}>
                        {relativeDayLabel(entry.completedAt)}
                        {SOURCE_LABEL[entry.source] && (
                            <span style={{ color: 'var(--mom-warm-gray)', fontSize: 12 }}> · {SOURCE_LABEL[entry.source]}</span>
                        )}
                    </span>
                    {confirmRemoveId === entry.id ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                            <button onClick={(e) => handleConfirm(e, entry.id)} disabled={removingId === entry.id} className="mom-btn"
                                style={{ background: 'none', border: 'none', cursor: removingId === entry.id ? 'default' : 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700, color: 'var(--mom-primary)', textDecoration: 'underline', padding: 0, opacity: removingId === entry.id ? 0.5 : 1 }}>
                                {removingId === entry.id ? '…' : (copy.removeConfirmLabel || 'Remove')}
                            </button>
                            <button onClick={(e) => { stop(e); setConfirmRemoveId(null); }} disabled={removingId === entry.id} className="mom-btn"
                                style={{ background: 'none', border: 'none', cursor: removingId === entry.id ? 'default' : 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-warm-gray)', padding: 0 }}>
                                {copy.removeKeepLabel || 'Keep'}
                            </button>
                        </span>
                    ) : (
                        <button onClick={(e) => { stop(e); setConfirmRemoveId(entry.id); }} className="mom-btn"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, color: 'var(--mom-warm-gray)', textDecoration: 'underline', padding: 0 }}>
                            {copy.historyRemoveLabel || 'Remove'}
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

export default CompletionHistoryList;
